import { readFile } from "node:fs/promises";
import { join } from "node:path";

import { discoverSkills, type Skill } from "./discover-skills.js";
import { stripSkillFrontmatter } from "./skill-frontmatter.js";

/**
 * Skill metadata exposed via `agent.skills.list()`. Full skill prompt bodies
 * are NEVER returned — only frontmatter fields.
 *
 * @internal
 */
export type SkillMetadata = Skill;

/**
 * A stored skill row. Filesystem skills are a bare {@link Skill}; inline
 * (`createSkill`) skills additionally carry their `instructions` body + optional
 * `references` on the object. Typing the store this way lets `get()` read those
 * fields directly, without a cast. @internal
 */
type StoredSkill = Skill & { instructions?: string; references?: Record<string, string> };

/** SE20 — a skill resolved with its body (+ SE21 references). @internal */
export interface SkillDetail {
  name: string;
  description: string;
  instructions: string;
  references?: Record<string, string>;
}

/** SE20 — the internal `agent.skills` handle shape (list + get), shared by the runtime wiring. @internal */
export interface SkillsHandle {
  /**
   * Lean listing — name + description ONLY. Inline skills carry their body +
   * references on the underlying object; the public handle projects them away so
   * `agent.skills.list()` never leaks a body. Full bodies come only via `get`.
   */
  list: () => Promise<Array<{ name: string; description: string }>>;
  get: (name: string) => Promise<SkillDetail | undefined>;
}

/**
 * File-based skills loader. Discovers `.theokit/skills/<name>/SKILL.md`
 * frontmatter when `local.settingSources` includes `"project"`.
 *
 * Delegates the discovery loop to the shared `discoverSkills` primitive (M4-1,
 * single source of truth, also public via `@theokit/sdk/skills`). Per the
 * strict-frontmatter ADR + EC-5, a malformed skill is excluded from `list()`
 * and emits a stderr warning; the agent run continues without it.
 *
 * @internal
 */
export class SkillsManager {
  private skills: StoredSkill[] = [];

  constructor(
    private readonly cwd: string,
    _enabled: string[] | undefined,
    private readonly settingSourcesIncludeProject: boolean,
    // M22 — optional custom skills directory + inline (code-defined) skills.
    private readonly skillsDir?: string,
    private readonly inline?: StoredSkill[],
  ) {
    void _enabled;
  }

  async initialize(): Promise<void> {
    // M22 — inline skills work even without project setting sources (they need no filesystem).
    if (!this.settingSourcesIncludeProject) {
      this.skills = this.mergeInline([]);
      return;
    }
    await this.refresh();
  }

  async refresh(): Promise<void> {
    // M22 — a custom skillsDir overrides the default `<cwd>/.theokit/skills` root.
    const skillsRoot = this.skillsDir ?? join(this.cwd, ".theokit", "skills");
    const discovered = await discoverSkills(skillsRoot, {
      onInvalidSkill: (info) => {
        process.stderr.write(
          `[theokit-sdk] skill ${info.name} skipped (${info.code}): ${info.message}\n`,
        );
      },
    });
    this.skills = this.mergeInline(discovered);
  }

  /** M22 — merge inline skills over discovered ones; inline wins on a name conflict. */
  private mergeInline(discovered: StoredSkill[]): StoredSkill[] {
    if (this.inline === undefined || this.inline.length === 0) return discovered;
    const inlineNames = new Set(this.inline.map((s) => s.name));
    return [...discovered.filter((s) => !inlineNames.has(s.name)), ...this.inline];
  }

  list(): Promise<SkillMetadata[]> {
    // Return every discovered skill — `enabled` is a runtime hint for which
    // skills the parent agent may invoke, not a visibility filter.
    return Promise.resolve(this.skills);
  }

  /**
   * SE20 — resolve a skill by name INCLUDING its body. Inline (`createSkill`)
   * skills carry `instructions` on the object; filesystem skills read the body
   * from their `source` SKILL.md (frontmatter stripped). `undefined` when no
   * enabled skill matches (malformed skills were already excluded at discovery).
   */
  async get(name: string): Promise<SkillDetail | undefined> {
    const skill = this.skills.find((s) => s.name === name);
    if (skill === undefined) return undefined;
    // Inline skills carry the body; filesystem skills re-read it from `source`.
    const instructions =
      typeof skill.instructions === "string"
        ? skill.instructions
        : stripSkillFrontmatter(await readFile(skill.source, "utf8"));
    // SE21 — inline skills may bundle `references`; filesystem skills carry none here.
    const references = skill.references;
    return {
      name: skill.name,
      description: skill.description,
      instructions,
      ...(references !== undefined ? { references } : {}),
    };
  }
}
